import {
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useStyles } from "./style";
import moment, { Moment } from "moment";
import { useMemo, forwardRef } from "react";
import { calStartTimeOfService } from "common/utils";
import { Remove } from "svgr/remove";

interface IProps {
  date: Moment;
  unixPickDateTime: number;
  services: Array<any>;
  isBooked?: boolean;
  includeBufferTime: boolean;
  onRemove?: (serviceId: string) => void;
}

type Service = {
  serviceId: string;
  serviceName: string;
  bufferTime: number;
  servicePrice: number;
  time: number;
  staffId: string;
  staffName: string;
};

const blueColor = "#0B70E1";
const greenColor = "#077A20";

const ServicesInformation = forwardRef<HTMLDivElement, IProps>(
  (
    { date, unixPickDateTime, services, isBooked, includeBufferTime, onRemove },
    ref
  ) => {
    const classes = useStyles();

    const listService = useMemo(() => {
      const temp: Record<number, Service[]> = {};

      services.forEach((service: Service, idx: number) => {
        const time = +calStartTimeOfService(
          services,
          service.staffId,
          unixPickDateTime,
          idx,
          includeBufferTime
        );

        if (!temp[time]) {
          temp[time] = [];
        }
        temp[time].push(service);
      });

      const newArray = Object.keys(temp).map((time) => ({
        startTime: +time,
        services: temp[+time],
      }));

      return newArray;
    }, [JSON.stringify(services)]);

    return (
      <Stack
        ref={ref}
        height={isBooked ? "100%" : "auto"}
        maxHeight={isBooked ? "100%" : "auto"}
      >
        <Box
          className={classes.title}
          borderColor={isBooked ? greenColor : blueColor}
          bgcolor={isBooked ? greenColor : blueColor}
        >
          <Typography color="#fff" fontWeight={700} fontSize={18}>
            {moment(date).format("dddd, MMMM DD, YYYY")}
          </Typography>
        </Box>
        <Box
          className={classes.mainContent}
          bgcolor={isBooked ? "#F8FFF6" : "#EBF4FD"}
          borderColor={isBooked ? greenColor : blueColor}
        >
          <Box maxHeight={isBooked ? "100%" : 200}>
            <List disablePadding>
              {listService.map((item: any, idx: number) => (
                <Box key={item.startTime}>
                  <ListItem
                    sx={{
                      flexDirection: "column",
                      listStyleType: "disc",
                      borderTop: idx === 0 ? "" : "1px dashed #96C6F6",
                      borderColor: isBooked ? "#919EAB" : "#73B2F8",
                      paddingTop: idx === 0 ? 0 : "",
                      paddingBottom: idx === listService.length - 1 ? 0 : "",
                    }}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      sx={{
                        display: "list-item",
                        "& .MuiTypography-root": {
                          fontSize: 14.5,
                          fontWeight: 700,
                        },
                      }}
                    >
                      {moment.unix(item.startTime).format("hh:mm A")}
                    </ListItemText>
                    <Grid container gap={1}>
                      {item.services.map((service: Service) => {
                        const { serviceId, serviceName, staffName } = service;
                        return (
                          <Grid
                            item
                            xs={12}
                            key={serviceId}
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            columnGap={1}
                          >
                            <Typography
                              fontSize={14}
                              sx={{
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flex: 1,
                              }}
                            >
                              {serviceName}
                            </Typography>
                            <Typography whiteSpace="nowrap" fontSize={14}>
                              with {staffName}
                            </Typography>
                            {onRemove && services.length > 1 && (
                              <IconButton
                                onClick={() => onRemove(serviceId)}
                                color="primary"
                                sx={{ mr: -1.5 }}
                              >
                                <Remove width="18px" height="18px" />
                              </IconButton>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </ListItem>
                </Box>
              ))}
            </List>
          </Box>
        </Box>
      </Stack>
    );
  }
);

export default ServicesInformation;
